import { useEffect, useState } from 'react';
import { getImagemByUserId, getUserByCPFAndCodUsoApi } from '../../Api';
import { useParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import Footer from '../../components/Footer';

const C = {
    ink: '#16161a',
    gold: '#e2e144',
    success: '#1f9d57',
    successBg: '#eaf7ee',
    text: '#3a3a42',
    muted: '#8a8a82',
    bg: '#f5f5f1',
    surface: '#ffffff',
    border: '#ececdf',
};

export default function ValidadorUnae() {
    const [imagem, setImagem] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const { cpf, codUso } = useParams();

    useEffect(() => {
        if (!cpf || !codUso) return;
        const carregar = async () => {
            try {
                setLoading(true);
                const resUser = await getUserByCPFAndCodUsoApi({ cpf, codUso });
                const userData = resUser?.data?.data;
                setCurrentUser(userData);
                if (userData?.id) {
                    const resImagem = await getImagemByUserId({ id: userData.id });
                    setImagem(resImagem?.data?.imagem);
                }
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            } finally {
                setLoading(false);
            }
        };
        carregar();
    }, [cpf, codUso]);

    if (loading) return <LoadingSkeleton />;
    if (!currentUser) return <StateMsg title="Documento não encontrado" subtitle="Verifique o QR Code ou o link de validação." danger />;

    return (
        <>
            <div style={s.page}>
                <div style={s.card}>
                    {/* Brand bar */}
                    <div style={s.brand}>
                        <img src={`${process.env.PUBLIC_URL}/nomeUnae.png`} alt="UNAE Estudantil" style={s.brandLogo} />
                        <span style={s.brandCaption}>Carteira de Identidade Estudantil</span>
                    </div>

                    {/* Status */}
                    <div style={s.status}>
                        <ShieldCheck size={18} />
                        <span style={s.statusDot} />
                        Documento válido
                    </div>

                    {/* Perfil: foto + nome */}
                    <div style={s.profile}>
                        <div style={s.photoWrap}>
                            {imagem
                                ? <img src={imagem} alt="Estudante" style={s.photo} />
                                : <div style={s.photoPh}>Sem foto</div>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={s.name}>{currentUser?.nome}</div>
                            <div style={s.inst}>{currentUser?.curso || '—'}</div>
                            <div style={s.instSub}>{currentUser?.instituicao || ''}</div>
                        </div>
                    </div>

                    {/* Atestação */}
                    <div style={s.atest}>
                        A <b>UNAE</b> atesta que <b>{currentUser?.nome?.toUpperCase()}</b> é estudante e encontra-se
                        regularmente matriculado(a) no curso de <b>{currentUser?.curso}</b> na instituição <b>{currentUser?.instituicao}</b>.
                    </div>

                    {/* Grid de dados */}
                    <div style={s.grid}>
                        <Field label="CPF" value={formatarCPF(cpf)} />
                        <Field label="Nascimento" value={currentUser?.dataNascimento} />
                        <Field label="Código de uso" value={currentUser?.codigoUso} />
                        <Field label="Emissor" value="UNAE" />
                    </div>

                    <div style={s.foot}>Validação oficial digital · UNAE</div>
                </div>
            </div>
            <Footer />
        </>
    );
}

function Field({ label, value }) {
    return (
        <div style={s.field}>
            <span style={s.fieldLabel}>{label}</span>
            <span style={s.fieldValue}>{value || '—'}</span>
        </div>
    );
}

// Loading "interativo": skeleton que imita o card real (foto, nome, dados) com
// shimmer + escudo pulsando + barra indeterminada. Dá a sensação de carregamento.
function LoadingSkeleton() {
    return (
        <div style={s.page}>
            <style>{`
                @keyframes vu-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                @keyframes vu-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.1); opacity: .8; } }
                @keyframes vu-bar { 0% { left: -45%; } 100% { left: 100%; } }
                .vu-sk { background: linear-gradient(90deg, #ededea 25%, #f7f7f4 50%, #ededea 75%); background-size: 200% 100%; animation: vu-shimmer 1.3s linear infinite; border-radius: 7px; }
            `}</style>
            <div style={s.card}>
                {/* Brand bar */}
                <div style={s.brand}>
                    <div style={s.brandLeft}>
                        <span style={{ ...s.dot, animation: 'vu-pulse 1.2s ease-in-out infinite' }}>U</span>
                        <div className="vu-sk" style={{ width: 92, height: 18 }} />
                    </div>
                    <div className="vu-sk" style={{ width: 88, height: 10 }} />
                </div>

                {/* Status "validando…" com barra indeterminada */}
                <div style={{ ...s.status, background: '#fafad0', color: '#8a7a14', position: 'relative', overflow: 'hidden' }}>
                    <ShieldCheck size={18} style={{ animation: 'vu-pulse 1.2s ease-in-out infinite' }} />
                    Validando credenciais…
                    <span style={{ position: 'absolute', bottom: 0, left: '-45%', width: '45%', height: 3, background: C.gold, borderRadius: 3, animation: 'vu-bar 1.1s ease-in-out infinite' }} />
                </div>

                {/* Perfil: foto + nome */}
                <div style={s.profile}>
                    <div className="vu-sk" style={{ width: 84, height: 112, borderRadius: 12, flex: '0 0 auto' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="vu-sk" style={{ width: '72%', height: 18, marginBottom: 10 }} />
                        <div className="vu-sk" style={{ width: '50%', height: 13, marginBottom: 8 }} />
                        <div className="vu-sk" style={{ width: '62%', height: 12 }} />
                    </div>
                </div>

                {/* Atestação */}
                <div style={{ ...s.atest, background: '#faf9f3', borderColor: C.border }}>
                    <div className="vu-sk" style={{ width: '100%', height: 11, marginBottom: 7 }} />
                    <div className="vu-sk" style={{ width: '94%', height: 11, marginBottom: 7 }} />
                    <div className="vu-sk" style={{ width: '68%', height: 11 }} />
                </div>

                {/* Grid de dados */}
                <div style={s.grid}>
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} style={s.field}>
                            <div className="vu-sk" style={{ width: 52, height: 9, marginBottom: 8 }} />
                            <div className="vu-sk" style={{ width: '80%', height: 13 }} />
                        </div>
                    ))}
                </div>

                <div className="vu-sk" style={{ width: '100%', height: 42, borderRadius: 10 }} />
            </div>
        </div>
    );
}

function StateMsg({ title, subtitle, danger }) {
    return (
        <div style={s.page}>
            <div style={{ ...s.card, textAlign: 'center', padding: '48px 28px' }}>
                <div style={{
                    width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
                    display: 'grid', placeItems: 'center',
                    background: danger ? '#fdecec' : '#fafad0',
                    fontSize: 26,
                }}>{danger ? '⚠️' : '🔎'}</div>
                <div style={{ fontWeight: 800, color: C.ink, fontSize: 18 }}>{title}</div>
                {subtitle && <div style={{ color: C.muted, marginTop: 6, fontSize: 14 }}>{subtitle}</div>}
            </div>
        </div>
    );
}

function formatarCPF(cpf) {
    if (!cpf) return '';
    return cpf.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

const s = {
    page: { backgroundColor: C.bg, minHeight: '100vh', display: 'flex', justifyContent: 'center', padding: '28px 14px', fontFamily: "'Inter', sans-serif" },
    card: { width: '100%', maxWidth: 460, background: C.surface, borderRadius: 24, padding: 26, border: `1px solid ${C.border}`, boxShadow: '0 18px 50px rgba(16,16,26,0.10)', height: 'fit-content' },

    brand: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 20, flexWrap: 'wrap' },
    brandLeft: { display: 'flex', alignItems: 'center', gap: 9 },
    dot: { width: 30, height: 30, borderRadius: 9, background: C.gold, color: C.ink, fontWeight: 800, display: 'grid', placeItems: 'center', fontSize: 15 },
    brandLogo: { height: 30, width: 'auto', display: 'block' },
    brandCaption: { fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },

    status: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: C.successBg, color: C.success, padding: '12px', borderRadius: 12, fontWeight: 700, fontSize: 14, marginBottom: 22 },
    statusDot: { width: 7, height: 7, borderRadius: '50%', background: C.success },

    profile: { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 },
    photoWrap: { width: 84, height: 112, flex: '0 0 auto', borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, background: '#f0f0ea' },
    photo: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
    photoPh: { width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: 11, color: '#aaa' },
    name: { fontWeight: 800, fontSize: 18, color: C.ink, lineHeight: 1.2 },
    inst: { fontSize: 13.5, color: C.text, fontWeight: 600, marginTop: 4 },
    instSub: { fontSize: 12.5, color: C.muted },

    atest: { background: '#fafad0', border: `1px solid #ecebb0`, borderRadius: 12, padding: '14px 16px', fontSize: 12.5, color: '#5f5e1a', lineHeight: 1.55, marginBottom: 20 },

    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 22 },
    field: { display: 'flex', flexDirection: 'column', gap: 2, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 },
    fieldLabel: { fontSize: 10, color: C.muted, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 },
    fieldValue: { fontSize: 14, color: C.ink, fontWeight: 600 },

    foot: { textAlign: 'center', fontSize: 11, color: C.muted, marginTop: 22 },
};
